import { logError } from '@/utils/logger';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash, MagnifyingGlass, X, MapPin, FilePdf } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ExportButton from '@/components/ExportButton';
import PhotoUpload from '@/components/PhotoUpload';
import RenewalStatusBadge from '@/components/RenewalStatusBadge';
import { SortableHeader, useSort } from '@/components/SortableHeader';

const Clients = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterZone, setFilterZone] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const [newSite, setNewSite] = useState('');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '',
    contract_number: '', contract_start: '', contract_end: '',
    sites: [], photo_url: '', renewal_status: 'none', zone_id: '',
  });
  const [zones, setZones] = useState([]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients`, { credentials: 'include' });
      if (response.ok) setClients(await response.json());
    } catch (error) {
      logError('Failed to fetch clients:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally { setLoading(false); }
  }, []);

  const fetchZones = useCallback(async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zones`, { credentials: 'include' });
      if (r.ok) setZones(await r.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchClients(); fetchZones(); }, [fetchClients, fetchZones]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.zone_id) { toast.error('La zone du client est obligatoire'); return; }
    try {
      const payload = { ...formData, photo_url: formData.photo_url || null };
      const url = editingClient
        ? `${process.env.REACT_APP_BACKEND_URL}/api/clients/${editingClient.client_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/clients`;
      const response = await fetch(url, {
        method: editingClient ? 'PUT' : 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success(editingClient ? 'Client modifié' : 'Client ajouté');
        setIsDialogOpen(false); resetForm(); fetchClients();
      } else { const err = await response.json(); toast.error(err.detail || 'Erreur'); }
    } catch (error) { logError('Save client:', error); toast.error('Erreur'); }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name, email: client.email, phone: client.phone,
      address: client.address, contract_number: client.contract_number || '',
      contract_start: client.contract_start?.split('T')[0] || '',
      contract_end: client.contract_end?.split('T')[0] || '',
      sites: client.sites || [], photo_url: client.photo_url || '',
      renewal_status: client.renewal_status || 'none',
      zone_id: client.zone_id || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Client supprimé'); fetchClients(); }
    } catch { toast.error('Erreur'); }
  };

  const updateRenewalStatus = async (clientId, status) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ renewal_status: status }),
      });
      if (res.ok) { toast.success('Statut mis à jour'); fetchClients(); }
      else toast.error('Erreur');
    } catch { toast.error('Erreur'); }
  };

  const updateZone = async (clientId, zoneId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zone_id: zoneId }),
      });
      if (res.ok) { toast.success('Zone mise à jour'); fetchClients(); }
      else toast.error('Erreur');
    } catch { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', address: '', contract_number: '', contract_start: '', contract_end: '', sites: [], photo_url: '', renewal_status: 'none', zone_id: '' });
    setEditingClient(null); setNewSite('');
  };

  const addSite = () => {
    const site = newSite.trim();
    if (site && !formData.sites.includes(site)) {
      setFormData({ ...formData, sites: [...formData.sites, site] });
      setNewSite('');
    }
  };

  const removeSite = (siteName) => {
    setFormData({ ...formData, sites: formData.sites.filter((s) => s !== siteName) });
  };

  // All unique sites for filter
  const allSites = [...new Set(clients.flatMap(c => c.sites || []))].sort();
  const { sortConfig, onSort, sortData } = useSort('name', 'asc');

  // Filtered and sorted clients
  const filteredClients = sortData(clients.filter(c => {
    const matchSearch = !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()) || (c.sites || []).some(s => s.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchSite = filterSite === 'all' || (c.sites || []).includes(filterSite);
    const matchZone = filterZone === 'all' || c.zone_id === filterZone;
    return matchSearch && matchSite && matchZone;
  }));

  if (loading) return <div className="text-[#94A3B8]">{t('common.loading')}</div>;

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight">{t('clients.title')}</h1>
          <p className="text-[#94A3B8] mt-1 text-sm sm:text-base">Gestion des clients et sites associés</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ExportButton endpoint="clients" filename="clients" label="Export Excel" />
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-client-button">
                <Plus size={20} className="mr-2" weight="bold" /> {t('clients.add')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">{editingClient ? 'Modifier le client' : t('clients.add')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex justify-center">
                  <PhotoUpload currentUrl={formData.photo_url} onUploaded={(url) => setFormData({ ...formData, photo_url: url })} size="lg" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#F8FAFC]">{t('clients.name')}</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="client-name-input" />
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC]">{t('clients.email')}</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#F8FAFC]">{t('clients.phone')}</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC]">N° Contrat</Label>
                    <Input value={formData.contract_number} onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#F8FAFC]">{t('clients.address')}</Label>
                  <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#F8FAFC]">Début contrat</Label>
                    <Input type="date" value={formData.contract_start} onChange={(e) => setFormData({ ...formData, contract_start: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC]">Fin contrat</Label>
                    <Input type="date" value={formData.contract_end} onChange={(e) => setFormData({ ...formData, contract_end: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#F8FAFC]">Zone du client <span className="text-[#EF4444]">*</span></Label>
                    <Select value={formData.zone_id} onValueChange={(v) => setFormData({ ...formData, zone_id: v })} required>
                      <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="client-zone-select">
                        <SelectValue placeholder="Choisir une zone..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                        {zones.map((z) => (
                          <SelectItem key={z.zone_id} value={z.zone_id} className="text-[#F8FAFC]">{z.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC]">Suivi renouvellement</Label>
                    <Select value={formData.renewal_status} onValueChange={(v) => setFormData({ ...formData, renewal_status: v })}>
                    <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="renewal-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                      <SelectItem value="none" className="text-[#F8FAFC]">— Aucun</SelectItem>
                      <SelectItem value="contacted" className="text-[#F8FAFC]">Contacté</SelectItem>
                      <SelectItem value="negotiating" className="text-[#F8FAFC]">En négociation</SelectItem>
                      <SelectItem value="renewed" className="text-[#F8FAFC]">Renouvelé</SelectItem>
                      <SelectItem value="lost" className="text-[#F8FAFC]">Perdu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                </div>

                {/* Sites dynamiques */}
                <div>
                  <Label className="text-[#F8FAFC]">Sites du client</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={newSite} onChange={(e) => setNewSite(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSite(); } }} placeholder="Nom du site..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
                    <Button type="button" onClick={addSite} className="bg-[#10B981] hover:bg-[#059669] text-white px-4">
                      <Plus size={16} />
                    </Button>
                  </div>
                  {formData.sites.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.sites.map((site) => (
                        <Badge key={site} className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30 px-3 py-1 flex items-center gap-1.5">
                          <MapPin size={12} /> {site}
                          <button type="button" onClick={() => removeSite(site)} className="ml-1 hover:text-[#EF4444]"><X size={12} /></button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">{t('common.cancel')}</Button>
                  <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-client-button">{t('common.save')}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher par nom ou site..." className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] pl-9" data-testid="search-client" />
        </div>
        {zones.length > 0 && (
          <Select value={filterZone} onValueChange={setFilterZone}>
            <SelectTrigger className="w-[180px] bg-[#0F172A] border-[#1E293B] text-[#F8FAFC]" data-testid="filter-zone-select">
              <SelectValue placeholder="Filtrer par zone" />
            </SelectTrigger>
            <SelectContent className="bg-[#0F172A] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">Toutes les zones</SelectItem>
              {zones.map(z => <SelectItem key={z.zone_id} value={z.zone_id} className="text-[#F8FAFC]">{z.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {allSites.length > 0 && (
          <Select value={filterSite} onValueChange={setFilterSite}>
            <SelectTrigger className="w-[200px] bg-[#0F172A] border-[#1E293B] text-[#F8FAFC]">
              <SelectValue placeholder="Filtrer par site" />
            </SelectTrigger>
            <SelectContent className="bg-[#0F172A] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">Tous les sites</SelectItem>
              {allSites.map(s => <SelectItem key={s} value={s} className="text-[#F8FAFC]">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Clients Table */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1E293B]/50">
              <tr>
                <SortableHeader label={t('clients.name')} sortKey="name" sortConfig={sortConfig} onSort={onSort} />
                <SortableHeader label={t('clients.phone')} sortKey="phone" sortConfig={sortConfig} onSort={onSort} />
                <SortableHeader label="N° Contrat" sortKey="contract_number" sortConfig={sortConfig} onSort={onSort} />
                <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">Zone</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">Renouvellement</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">{t('clients.sites')}</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1E293B]">
              {filteredClients.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-8 text-center text-[#94A3B8]">Aucun client trouvé</td></tr>
              ) : filteredClients.map((client) => (
                <tr key={client.client_id} className="hover:bg-[#1E293B]/30 transition-colors" data-testid={`client-row-${client.client_id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {client.photo_url ? (
                        <img src={client.photo_url} alt={client.name} className="h-9 w-9 rounded-full object-cover border border-[#334155]" />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-medium">{client.name?.charAt(0)}</div>
                      )}
                      <div>
                        <span className="text-[#F8FAFC] font-medium">{client.name}</span>
                        <p className="text-xs text-[#94A3B8]">{client.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[#94A3B8]">{client.phone}</td>
                  <td className="px-6 py-4 text-[#94A3B8]">{client.contract_number || '-'}</td>
                  <td className="px-6 py-4 text-[#94A3B8] text-xs">
                    <Select value={client.zone_id || ''} onValueChange={(v) => updateZone(client.client_id, v)}>
                      <SelectTrigger className="h-7 bg-transparent border-[#334155] text-[#94A3B8] hover:bg-[#1E293B] w-[130px] text-xs" data-testid={`client-zone-${client.client_id}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                        {zones.map(z => <SelectItem key={z.zone_id} value={z.zone_id} className="text-[#F8FAFC]">{z.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4">
                    <RenewalStatusBadge value={client.renewal_status} onChange={(v) => updateRenewalStatus(client.client_id, v)} clientId={client.client_id} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(client.sites || []).length > 0 ? client.sites.map((s) => (
                        <Badge key={s} className="bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30 text-xs">{s}</Badge>
                      )) : <span className="text-[#94A3B8]">-</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(client)} className="text-[#2563EB] hover:text-white hover:bg-[#2563EB]"><Pencil size={16} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${client.client_id}/pdf`, '_blank')} className="text-[#10B981] hover:text-white hover:bg-[#10B981]" title="Fiche PDF"><FilePdf size={16} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(client.client_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444]"><Trash size={16} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-[#1E293B]">
          {filteredClients.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#94A3B8]">Aucun client trouvé</div>
          ) : filteredClients.map((client) => (
            <div key={client.client_id} className="p-4 space-y-3" data-testid={`client-card-${client.client_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  {client.photo_url ? (
                    <img src={client.photo_url} alt={client.name} className="h-10 w-10 rounded-full object-cover border border-[#334155] flex-shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-medium flex-shrink-0">{client.name?.charAt(0)}</div>
                  )}
                  <div className="min-w-0">
                    <p className="text-[#F8FAFC] font-medium truncate">{client.name}</p>
                    <p className="text-xs text-[#94A3B8] truncate">{client.email}</p>
                  </div>
                </div>
                <div className="flex flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(client)} className="text-[#2563EB] hover:text-white hover:bg-[#2563EB] h-8 w-8 p-0"><Pencil size={15} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${client.client_id}/pdf`, '_blank')} className="text-[#10B981] hover:text-white hover:bg-[#10B981] h-8 w-8 p-0"><FilePdf size={15} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(client.client_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] h-8 w-8 p-0"><Trash size={15} /></Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-[10px] uppercase tracking-wider text-[#64748B]">{t('clients.phone')}</p><p className="text-[#94A3B8]">{client.phone || '-'}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-[#64748B]">N° Contrat</p><p className="text-[#94A3B8]">{client.contract_number || '-'}</p></div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-[#64748B]">Renouvellement:</span>
                <RenewalStatusBadge value={client.renewal_status} onChange={(v) => updateRenewalStatus(client.client_id, v)} clientId={client.client_id} />
              </div>
              {(client.sites || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {client.sites.map((s) => (
                    <Badge key={s} className="bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30 text-xs">{s}</Badge>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Clients;
